import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchOverageCapacity,
  imogenSelector,
} from "../../../../../redux/slices/imogen";

const STALENESS_HOURS = 48;

const formatPct = (value) => {
  if (value == null) return "—";
  return `${Number(value).toFixed(1)}%`;
};

const formatSourceTimestamp = (iso) => {
  if (!iso) return "unknown";
  return moment.utc(iso).format("MMM D, YYYY");
};

const isStale = (iso) => {
  if (!iso) return true;
  return moment.utc().diff(moment.utc(iso), "hours") > STALENESS_HOURS;
};

const OverageCapacity = ({ store }) => {
  const dispatch = useDispatch();
  const {
    overageCapacity: data,
    overageCapacityLoading: loading,
    overageCapacityErrorMessage: errorMessage,
  } = useSelector(imogenSelector);

  useEffect(() => {
    dispatch(fetchOverageCapacity({ store }));
  }, [dispatch, store]);

  const sourceFreshness = useMemo(() => {
    if (!data?.data_freshness) return null;
    const ts = data.data_freshness.capacity_monitor_latest;
    return { timestamp: ts, stale: isStale(ts) };
  }, [data]);

  const sortedTiles = useMemo(() => {
    if (!data?.tiles) return [];
    return [...data.tiles].sort(
      (a, b) => (b.usage_pct ?? 0) - (a.usage_pct ?? 0)
    );
  }, [data]);

  if (!store || store === "all") {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          Please select a specific store to view overage capacity.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="error">
        Failed to load overage capacity: {errorMessage}
      </Alert>
    );
  }

  if (!data) return null;

  const lastRefresh = data.computed_at
    ? moment.utc(data.computed_at).format("MMM D, HH:mm [UTC]")
    : null;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        mb={1}
      >
        <Box>
          <Typography variant="h6">
            Capacity monitor
            {sortedTiles.length > 0 ? ` (${sortedTiles.length})` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Account / marketplace / size-tier combinations at 80%+ of allocated
            FBA capacity.
          </Typography>
        </Box>
        {lastRefresh && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastRefresh}
          </Typography>
        )}
      </Box>

      {sourceFreshness && (
        <Typography
          variant="caption"
          color={sourceFreshness.stale ? "warning.main" : "text.secondary"}
          sx={{ display: "block", mb: 2 }}
        >
          Capacity monitor last updated{" "}
          {formatSourceTimestamp(sourceFreshness.timestamp)}
          {sourceFreshness.stale && " — data may be stale"}
        </Typography>
      )}

      {sortedTiles.length === 0 ? (
        <Box p={3}>
          <Typography variant="body2" color="text.secondary">
            No storage tiers at or above 80% capacity.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Used</TableCell>
                <TableCell align="right">Current (ft³)</TableCell>
                <TableCell align="right">Limit (ft³)</TableCell>
                {sortedTiles.some((t) => t.days_above_threshold != null) && (
                  <TableCell align="right">Days ≥ 80%</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTiles.map((tile, i) => (
                <TableRow key={`${tile.store}-${tile.storage_type}-${i}`}>
                  <TableCell>{tile.store}</TableCell>
                  <TableCell>{tile.storage_type}</TableCell>
                  <TableCell align="right">
                    {formatPct(tile.usage_pct)}
                  </TableCell>
                  <TableCell align="right">
                    {tile.current_cubic_feet != null
                      ? Number(tile.current_cubic_feet).toLocaleString(
                          undefined,
                          { maximumFractionDigits: 0 }
                        )
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {tile.capacity_limit_cubic_feet != null
                      ? Number(tile.capacity_limit_cubic_feet).toLocaleString(
                          undefined,
                          {
                            maximumFractionDigits: 0,
                          }
                        )
                      : "—"}
                  </TableCell>
                  {sortedTiles.some((t) => t.days_above_threshold != null) && (
                    <TableCell align="right">
                      {tile.days_above_threshold ?? "—"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OverageCapacity;
